import React, { useState, useEffect } from 'react'
import { C, LiveDot, Mono, Avatar } from '../components/ui'
import PlayerDrawer from '../components/PlayerDrawer'
import { getLeaderboard, todayStr } from '../api/client'

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <div onClick={onChange} style={{ width: 42, height: 24, borderRadius: 12, background: on ? C.blue : C.bk3, border: `1px solid ${on ? C.blueBdr : C.border}`, position: 'relative', cursor: 'pointer', transition: 'all .2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: on ? 20 : 3, width: 16, height: 16, borderRadius: 8, background: on ? '#fff' : C.dim, transition: 'left .2s' }} />
    </div>
  )
}

const SETTINGS_DEF = [
  { key: 'lineMove',   label: '📈 Line Movements',      sub: 'When a line moves 0.5+ points' },
  { key: 'sharpMoney', label: '💰 Sharp Money Alerts',  sub: 'When big money hits one side' },
  { key: 'whaleNew',   label: '🐳 New Top Plays',       sub: 'When a high confidence play appears' },
  { key: 'edgeMove',   label: '⚡ Edge Changes',        sub: 'When edge increases to 5%+' },
  { key: 'gameStart',  label: '⏱ Game Starting Soon',  sub: '30 min before first pitch' },
]

// ── Alert types ───────────────────────────────────────────────────────────────
const ALERT_TYPES = {
  lineMove:   { icon: '📈', color: '#F59E0B' },
  sharpMoney: { icon: '💰', color: C.green   },
  whale:      { icon: '🐳', color: C.blue    },
  edge:       { icon: '⚡', color: C.green   },
  gameStart:  { icon: '⏱', color: C.dim     },
  odds:       { icon: '🚨', color: '#EF4444' },
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000
  if (diff < 60)  return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  return `${Math.floor(diff / 3600)}h ago`
}

// ── Alert Card ────────────────────────────────────────────────────────────────
function AlertCard({ alert, onPlayerClick }) {
  const type  = ALERT_TYPES[alert.type] || { icon: '🔔', color: C.dim }
  const color = alert.urgent ? type.color : C.dim

  return (
    <div
      onClick={() => alert.player && onPlayerClick(alert.player)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '12px 16px', borderBottom: `1px solid ${C.dim2}`,
        borderLeft: `3px solid ${alert.urgent ? type.color : 'transparent'}`,
        cursor: alert.player ? 'pointer' : 'default',
        background: alert.urgent ? `${type.color}06` : 'transparent',
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: alert.urgent ? `${type.color}18` : C.bk3,
        border: `1px solid ${alert.urgent ? type.color + '44' : C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
      }}>
        {type.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, color: C.text }}>{alert.title}</span>
          <span style={{ fontSize: 9, color: C.dim, flexShrink: 0, marginLeft: 8, fontFamily: "'IBM Plex Mono', monospace" }}>{timeAgo(alert.ts)}</span>
        </div>
        <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.4 }}>{alert.body}</div>
        {alert.player && (
          <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 5, background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 6, padding: '2px 8px' }}>
            <span style={{ fontSize: 10, color: C.blue, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>VIEW BREAKDOWN →</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Live Line Feed ────────────────────────────────────────────────────────────
function LineFeedRow({ item }) {
  const dir = item.move > 0 ? '▲' : '▼'
  const color = item.move > 0 ? C.green : C.red
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: `1px solid ${C.dim2}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{item.player}</div>
        <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{item.market} · {item.book}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: C.dim }}>
          {item.from} <span style={{ color, fontWeight: 700 }}>{dir} {item.to}</span>
        </div>
        <div style={{ fontSize: 9, color, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>
          {item.move > 0 ? '+' : ''}{item.move} pts
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AlertsTab() {
  const [tab,           setTab]           = useState('alerts')
  const [settings,      setSettings]      = useState(() => Object.fromEntries(SETTINGS_DEF.map(s => [s.key, true])))
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [entries,       setEntries]       = useState([])
  const today = todayStr()

  // Load top plays to generate smart alerts
  useEffect(() => {
    getLeaderboard('HRR_over_1.5', today, 15).then(d => setEntries(d?.entries || [])).catch(() => {})
  }, [today])

  // Generate alerts from live data
  const now = Date.now()
  const liveAlerts = [
    ...entries.slice(0, 3).map((e, i) => ({
      id:     `whale-${e.player_id}`,
      type:   'whale',
      title:  `🐳 ${e.full_name} — High Confidence`,
      body:   `${(parseFloat(e.prob_over || 0) * 100).toFixed(0)}% hit chance · ${e.team} vs ${(e.opp_pitcher_name || 'TBD').split(' ').slice(-1)[0]}`,
      ts:     new Date(now - i * 4 * 60000).toISOString(),
      urgent: parseFloat(e.prob_over || 0) >= 0.70,
      player: e,
    })),
    ...entries.filter(e => e.edge_pct && e.edge_pct > 0.04).slice(0, 2).map((e, i) => ({
      id:     `edge-${e.player_id}`,
      type:   'edge',
      title:  `⚡ Edge Alert — ${e.full_name}`,
      body:   `+${(e.edge_pct * 100).toFixed(1)}% edge vs book · Book: ${e.book_over || '—'}`,
      ts:     new Date(now - (i + 2) * 7 * 60000).toISOString(),
      urgent: true,
      player: e,
    })),
    {
      id:     'gametime-1',
      type:   'gameStart',
      title:  '⏱ Games Starting Soon',
      body:   'First pitch in ~3h 40min · Lock your plays now',
      ts:     new Date(now - 15 * 60000).toISOString(),
      urgent: false,
      player: null,
    },
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts))

  // Simulated line feed (ready for real data wire-in)
  const lineFeed = [
    { player: entries[0]?.full_name || 'Matt Olson',    market: 'HRR O1.5',  book: 'FD',    from: '-115', to: '-130', move: -0.5 },
    { player: entries[1]?.full_name || 'Brandon Lowe',  market: 'HRR O1.5',  book: 'DK',    from: '-110', to: '-125', move: -0.5 },
    { player: entries[2]?.full_name || 'Drake Baldwin', market: 'TB O1.5',   book: 'ESPN+', from: '-120', to: '-105', move: 0.5  },
    { player: 'Freddie Freeman',                        market: 'HR Anytime', book: 'FD',    from: '+320', to: '+280', move: -1.0 },
    { player: 'Trea Turner',                            market: 'HRR O1.5',  book: 'DK',    from: '+110', to: '+125', move: 0.5  },
  ]

  const tabs = [
    { id: 'alerts', label: 'Alerts',     count: liveAlerts.filter(a => a.urgent).length },
    { id: 'lines',  label: 'Line Feed',  count: lineFeed.length },
    { id: 'settings', label: 'Settings', count: 0 },
  ]

  return (
    <div style={{ paddingBottom: 84 }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: `linear-gradient(180deg, ${C.bk} 85%, transparent)` }}>
        <div style={{ padding: '16px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.blue, fontSize: 20 }}>◆</span>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 22 }}>Alerts</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.blueBg, border: `1px solid ${C.blueBdr}`, borderRadius: 8, padding: '4px 10px' }}>
            <LiveDot size={5} />
            <span style={{ fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: C.blue, letterSpacing: '.06em' }}>LIVE</span>
          </div>
        </div>
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginTop: 12 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, background: 'none', border: 'none', padding: '9px 0 11px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', color: tab === t.id ? C.blue : C.dim, borderBottom: `2px solid ${tab === t.id ? C.blue : 'transparent'}`, transition: 'all .18s' }}>
              {t.label}{t.count > 0 && <span style={{ marginLeft: 5, background: tab === t.id ? C.blueBg : `${C.red}20`, border: `1px solid ${tab === t.id ? C.blueBdr : `${C.red}40`}`, borderRadius: 4, padding: '1px 5px', fontSize: 10, color: tab === t.id ? C.blue : C.red }}>{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Alerts Tab ── */}
      {tab === 'alerts' && (
        <div>
          {liveAlerts.length === 0 ? (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: C.dim, fontSize: 13 }}>No alerts yet — checking for plays...</div>
          ) : (
            <div style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 14, margin: '16px 16px 0', overflow: 'hidden' }}>
              {liveAlerts.map(a => <AlertCard key={a.id} alert={a} onPlayerClick={setSelectedPlayer} />)}
            </div>
          )}
        </div>
      )}

      {/* ── Line Feed Tab ── */}
      {tab === 'lines' && (
        <div>
          <div style={{ padding: '12px 16px 6px', fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
            LIVE LINE MOVEMENTS · UPDATES EVERY 5MIN
          </div>
          <div style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 14, margin: '0 16px', overflow: 'hidden' }}>
            {lineFeed.map((item, i) => <LineFeedRow key={i} item={item} />)}
          </div>
          <div style={{ padding: '12px 16px', fontSize: 10, color: C.dim, textAlign: 'center' }}>
            Wire book odds feed to get real-time line movements
          </div>
        </div>
      )}

      {/* ── Settings Tab ── */}
      {tab === 'settings' && (
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>Alert Preferences</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SETTINGS_DEF.map(s => (
              <div key={s.key} style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '13px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{s.sub}</div>
                </div>
                <Toggle on={settings[s.key]} onChange={() => setSettings(p => ({ ...p, [s.key]: !p[s.key] }))} />
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedPlayer && (
        <PlayerDrawer player={selectedPlayer} onClose={() => setSelectedPlayer(null)} onSavePlay={null} />
      )}
    </div>
  )
}
